import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Table, Tag, Button, message, Modal, Descriptions, Select, Space, Typography, Input, Drawer, Image } from 'antd';
import { EyeOutlined, DownOutlined, CloseOutlined, FilterOutlined, MailOutlined, PhoneOutlined, SwapOutlined } from '@ant-design/icons';
import { apiClient } from 'Utils/api';
import { useIsMobile } from 'Utils/useIsMobile';
import MobileListLayout from '../Components/MobileList/MobileListLayout';
import MobileCard from '../Components/MobileList/MobileCard';
import OptionSheet from '../Components/MobileList/OptionSheet';
import { Chip } from '../Components/ProductMobileList/ProductMobileList.style';

const { Title } = Typography;
const { TextArea } = Input;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  NEW: { label: 'Neu', color: 'blue' },
  CONTACTED: { label: 'Kontaktiert', color: 'cyan' },
  NEGOTIATING: { label: 'Verhandlung', color: 'gold' },
  ACCEPTED: { label: 'Angenommen', color: 'green' },
  REJECTED: { label: 'Abgelehnt', color: 'red' },
  CLOSED: { label: 'Geschlossen', color: 'default' },
};

const CONDITION_LABELS: Record<string, string> = {
  NEW: 'Neu / Ungetragen',
  LIKE_NEW: 'Neuwertig',
  VERY_GOOD: 'Sehr gut',
  GOOD: 'Gut',
  WORN: 'Gebrauchsspuren',
  REPAIR_NEEDED: 'Reparaturbedürftig',
};

const MOBILE_SORT_OPTIONS = [
  { value: 'createdAt:DESC', label: 'Neueste zuerst' },
  { value: 'createdAt:ASC', label: 'Älteste zuerst' },
  { value: 'desiredPriceCents:DESC', label: 'Wunschpreis (absteigend)' },
  { value: 'desiredPriceCents:ASC', label: 'Wunschpreis (aufsteigend)' },
  { value: 'brand:ASC', label: 'Marke (A–Z)' },
];

const MOBILE_PAGE_SIZE = 20;

interface SellRequest {
  id: string;
  brand: string;
  model: string;
  referenceNumber: string | null;
  yearOfProduction: number | null;
  condition: string | null;
  hasBox: boolean;
  hasPapers: boolean;
  desiredPriceCents: number | null;
  imageUrls: string[] | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string | null;
  customerMessage: string | null;
  status: string;
  internalNotes: string | null;
  createdAt: string;
}

const SellRequestsPage: React.FC = () => {
  const [items, setItems] = useState<SellRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<SellRequest | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('createdAt:DESC');
  const [sheet, setSheet] = useState<'filter' | 'sort' | null>(null);
  const [statusTarget, setStatusTarget] = useState<SellRequest | null>(null);
  const [shown, setShown] = useState(MOBILE_PAGE_SIZE);

  useEffect(() => { fetchAll(); }, []);

  // Open the request linked from the notification email (/…/:id)
  const { id } = useParams();
  const deepLinkOpened = useRef(false);
  useEffect(() => {
    const r = items.find((i) => i.id === id);
    if (r && !deepLinkOpened.current) { deepLinkOpened.current = true; openDetails(r); }
  }, [items, id]);

  useEffect(() => { setShown(MOBILE_PAGE_SIZE); }, [search, statusFilter, sort]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/sell-requests');
      setItems(res.data.sellRequests || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiClient.patch(`/api/admin/sell-requests/${id}`, { status });
      message.success('Status aktualisiert');
      fetchAll();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await apiClient.patch(`/api/admin/sell-requests/${selected.id}`, { internalNotes: notesDraft });
      setSelected({ ...selected, internalNotes: notesDraft });
      message.success('Notizen gespeichert');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    } finally {
      setSavingNotes(false);
    }
  };

  const formatPrice = (cents: number | null) =>
    cents ? `${(cents / 100).toLocaleString('de-DE')} €` : '–';

  const apiBase = process.env.REACT_APP_API_URL || '';

  const columns = [
    {
      title: 'Datum',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => new Date(v).toLocaleString('de-DE'),
    },
    { title: 'Marke', dataIndex: 'brand', key: 'brand' },
    { title: 'Modell', dataIndex: 'model', key: 'model' },
    {
      title: 'Kunde',
      key: 'customer',
      render: (_: any, r: SellRequest) => (
        <div>
          <div>{r.customerName}</div>
          <small style={{ color: '#888' }}>{r.customerEmail}</small>
        </div>
      ),
    },
    {
      title: 'Wunsch',
      dataIndex: 'desiredPriceCents',
      key: 'desiredPriceCents',
      render: formatPrice,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status: string, r: SellRequest) => (
        <Select
          value={status}
          style={{ width: 160 }}
          onChange={(v) => updateStatus(r.id, v)}
          options={Object.entries(STATUS_LABELS).map(([k, v]) => ({
            value: k,
            label: <Tag color={v.color}>{v.label}</Tag>,
          }))}
        />
      ),
    },
    {
      title: 'Aktion',
      key: 'action',
      width: 100,
      render: (_: any, r: SellRequest) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => {
            setSelected(r);
            setNotesDraft(r.internalNotes || '');
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  const openDetails = (r: SellRequest) => {
    setSelected(r);
    setNotesDraft(r.internalNotes || '');
  };

  const statusOptions = Object.entries(STATUS_LABELS).map(([value, v]) => ({ value, label: <Tag color={v.color}>{v.label}</Tag> }));

  const detailContent = selected && (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {isMobile && (
        <Select
          value={selected.status}
          style={{ width: '100%' }}
          onChange={(v) => updateStatus(selected.id, v)}
          options={statusOptions}
          data-testid="mobile-detail-status"
        />
      )}
      <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label="Marke">{selected.brand}</Descriptions.Item>
        <Descriptions.Item label="Modell">{selected.model}</Descriptions.Item>
        <Descriptions.Item label="Referenz">{selected.referenceNumber || '–'}</Descriptions.Item>
        <Descriptions.Item label="Baujahr">{selected.yearOfProduction || '–'}</Descriptions.Item>
        <Descriptions.Item label="Zustand">
          {selected.condition ? CONDITION_LABELS[selected.condition] : '–'}
        </Descriptions.Item>
        <Descriptions.Item label="Wunschpreis">{formatPrice(selected.desiredPriceCents)}</Descriptions.Item>
        <Descriptions.Item label="Box">{selected.hasBox ? 'Ja' : 'Nein'}</Descriptions.Item>
        <Descriptions.Item label="Papiere">{selected.hasPapers ? 'Ja' : 'Nein'}</Descriptions.Item>
        <Descriptions.Item label="Name">{selected.customerName}</Descriptions.Item>
        <Descriptions.Item label="E-Mail">
          <a href={`mailto:${selected.customerEmail}`}>{selected.customerEmail}</a>
        </Descriptions.Item>
        <Descriptions.Item label="Telefon" span={2}>
          {selected.customerPhone ? (
            <a href={`tel:${selected.customerPhone}`}>{selected.customerPhone}</a>
          ) : '–'}
        </Descriptions.Item>
        <Descriptions.Item label="Nachricht" span={2}>
          {selected.customerMessage || '–'}
        </Descriptions.Item>
      </Descriptions>

      {selected.imageUrls && selected.imageUrls.length > 0 && (
        <div>
          <Title level={5}>Bilder</Title>
          <Image.PreviewGroup>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
              {selected.imageUrls.map((url) => (
                <Image
                  key={url}
                  src={`${apiBase}${url}`}
                  alt=""
                  width="100%"
                  height={100}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  data-testid="sell-request-image"
                />
              ))}
            </div>
          </Image.PreviewGroup>
        </div>
      )}

      <div>
        <Title level={5}>Interne Notizen</Title>
        <TextArea
          rows={4}
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
        />
        <div style={{ textAlign: 'right', marginTop: 8 }}>
          <Button type="primary" loading={savingNotes} onClick={saveNotes}>
            Notizen speichern
          </Button>
        </div>
      </div>
    </Space>
  );

  if (isMobile) {
    const term = search.toLowerCase();
    const [sortField, sortOrder] = sort.split(':');
    const visible = items
      .filter((r) => !statusFilter || r.status === statusFilter)
      .filter((r) => !term || [r.brand, r.model, r.referenceNumber, r.customerName, r.customerEmail].some((v) => v?.toLowerCase().includes(term)))
      .sort((a: any, b: any) => {
        const x = a[sortField] ?? '';
        const y = b[sortField] ?? '';
        const result = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'de');
        return sortOrder === 'ASC' ? result : -result;
      });
    const filtered = !!search || !!statusFilter;
    return (
      <>
        <MobileListLayout
          title="Verkaufsanfragen"
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Marke, Modell oder Kunde …"
          filters={statusFilter ? (
            <Chip type="button" onClick={() => setStatusFilter('')} data-testid="mobile-chip-status">
              <span>{STATUS_LABELS[statusFilter]?.label}</span>
              <CloseOutlined />
            </Chip>
          ) : (
            <Button icon={<FilterOutlined />} onClick={() => setSheet('filter')} data-testid="mobile-status-open">Status <DownOutlined /></Button>
          )}
          count={`${visible.length} ${visible.length === 1 ? 'Verkaufsanfrage' : 'Verkaufsanfragen'}`}
          sortLabel={MOBILE_SORT_OPTIONS.find((o) => o.value === sort)?.label}
          onSortOpen={() => setSheet('sort')}
          loading={loading}
          isEmpty={visible.length === 0}
          emptyTitle="Keine Verkaufsanfragen gefunden"
          emptyText={filtered ? 'Keine Anfrage entspricht den aktuellen Filtern.' : 'Neue Anfragen aus dem Shop erscheinen hier.'}
          onReset={filtered ? () => { setSearch(''); setStatusFilter(''); } : undefined}
          hasMore={visible.length > shown}
          onLoadMore={() => setShown((n) => n + MOBILE_PAGE_SIZE)}
        >
          {visible.slice(0, shown).map((r) => (
            <MobileCard
              key={r.id}
              testId={`mobile-sell-card-${r.id}`}
              overline={new Date(r.createdAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
              title={`${r.brand} ${r.model}`}
              highlight={(
                <>
                  {formatPrice(r.desiredPriceCents)}
                  <Tag color={STATUS_LABELS[r.status]?.color}>{STATUS_LABELS[r.status]?.label || r.status}</Tag>
                </>
              )}
              meta={`${r.customerName} · ${r.customerEmail}`}
              onOpen={() => openDetails(r)}
              actions={[
                { key: 'details', label: 'Details', icon: <EyeOutlined />, onClick: () => openDetails(r) },
                { key: 'status', label: 'Status ändern', icon: <SwapOutlined />, onClick: () => setStatusTarget(r) },
                { key: 'mail', label: 'E-Mail schreiben', icon: <MailOutlined />, divider: true, onClick: () => { window.location.href = `mailto:${r.customerEmail}`; } },
                ...(r.customerPhone ? [{ key: 'call', label: 'Anrufen', icon: <PhoneOutlined />, onClick: () => { window.location.href = `tel:${r.customerPhone}`; } }] : []),
              ]}
            />
          ))}
        </MobileListLayout>
        <OptionSheet
          open={sheet === 'filter'}
          title="Status"
          value={statusFilter}
          options={[{ value: '', label: 'Alle Status' }, ...statusOptions]}
          onClose={() => setSheet(null)}
          onChange={(value) => { setStatusFilter(value); setSheet(null); }}
        />
        <OptionSheet
          open={sheet === 'sort'}
          title="Sortieren nach"
          value={sort}
          options={MOBILE_SORT_OPTIONS}
          onClose={() => setSheet(null)}
          onChange={(value) => { setSort(value); setSheet(null); }}
        />
        <OptionSheet
          open={!!statusTarget}
          title="Status ändern"
          value={statusTarget?.status}
          options={statusOptions}
          onClose={() => setStatusTarget(null)}
          onChange={(value) => { if (statusTarget) updateStatus(statusTarget.id, value); setStatusTarget(null); }}
        />
        <Drawer
          placement="bottom"
          open={!!selected}
          onClose={() => setSelected(null)}
          title={selected ? `${selected.brand} ${selected.model} – ${selected.customerName}` : ''}
          size="90vh"
          styles={{ wrapper: { borderRadius: '16px 16px 0 0', overflow: 'hidden' } }}
        >
          {detailContent}
        </Drawer>
      </>
    );
  }

  return (
    <div>
      <Title level={3}>Verkaufsanfragen</Title>
      <Card>
        <Table
          rowKey="id"
          dataSource={items}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={selected ? `${selected.brand} ${selected.model} – ${selected.customerName}` : ''}
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={null}
        width={760}
      >
        {detailContent}
      </Modal>
    </div>
  );
};

export default SellRequestsPage;
